import React from 'react'
import ReactDOM from 'react-dom'
import { useParams, useNavigate } from 'react-router-dom'
import CrudForm from '../../crud/CrudForm'
import { rentService as service, cartypeService, usercompaniesService } from '../../../services'
import { useTranslation } from 'react-i18next'
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material'
import ZonePointPicker, { PointsValue } from './ZonePointPicker'
import { resolveBlock } from '../../common/blockAction'
import { useBlockToast } from '../../common/BlockFeedback'

const eur = (n?: number | null) =>
    (n ?? 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })

/** Suma del coste de zona de los dos puntos, para desglosarlo en pantalla. */
const zoneCostOf = (points: PointsValue) =>
    (points.start?.zone?.price || 0) + (points.end?.zone?.price || 0)

/**
 * Lo que se manda al backend de cada punto: la coordenada y el id de la zona. El importe NO viaja
 * desde el navegador — el servidor lo relee de BD y lo congela (MOBD-98).
 */
const zonePayload = (points: PointsValue) => ({
    lat_point_start: points.start ? points.start.lat : null,
    lng_point_start: points.start ? points.start.lng : null,
    lat_point_end: points.end ? points.end.lat : null,
    lng_point_end: points.end ? points.end.lng : null,
    zoneStarReserve: points.start?.zone ? { id: points.start.zone.id } : null,
    zoneEndReserve: points.end?.zone ? { id: points.end.zone.id } : null,
})

const normalizeText = (text?: string) =>
    (text || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()

/** YYYY-MM-DDTHH:mm:ssZ (sin milisegundos) */
const toIso = (inputElement?: HTMLInputElement | null) => {
    if (!inputElement) return null
    let parsedDate: Date | null = null

    if (inputElement.type === 'datetime-local' && !Number.isNaN(inputElement.valueAsNumber) && inputElement.valueAsNumber > 0) {
        parsedDate = new Date(inputElement.valueAsNumber)
    } else if (inputElement.type === 'date' && inputElement.value) {
        const [y, m, day] = inputElement.value.split('-').map(Number)
        parsedDate = new Date(y, (m || 1) - 1, day || 1, 0, 0, 0)
    } else if (inputElement.value) {
        const tmp = new Date(inputElement.value)
        if (!isNaN(tmp.getTime())) parsedDate = tmp
    }
    if (!parsedDate) {
        return null
    }
    
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${parsedDate.getUTCFullYear()}-${pad(parsedDate.getUTCMonth() + 1)}-${pad(
        parsedDate.getUTCDate()
    )}T${pad(parsedDate.getUTCHours())}:${pad(parsedDate.getUTCMinutes())}:${pad(
        parsedDate.getUTCSeconds()
    )}Z`
}

//PriceBox
const PriceBox: React.FC<{ points: PointsValue }> = ({ points }) => {
    const { t } = useTranslation()
    const { showBlock } = useBlockToast()
    const [loading, setLoading] = React.useState(false)
    const [warn, setWarn] = React.useState<string | null>(null)
    const [types, setTypes] = React.useState<Array<{ id: number; label: string }>>([])
    const [total, setTotal] = React.useState(0)
    const [day, setDay] = React.useState(0)
    // ¿Hay un total de verdad? Si faltan fechas o tipo de coche no se puede calcular, y enseñar
    // "Total: 0,00 €" al lado de un coste de zona de 2,99 € confundía en el QA: parecía un fallo
    // de cálculo cuando en realidad solo faltaban datos.
    const [computed, setComputed] = React.useState(false)
    // Config de recargo de cancelación (empresa), leída del backend para el aviso (no hardcodear).
    const [cancelCfg, setCancelCfg] = React.useState<{
        amount: number
        hours: number
    }>({ amount: 49, hours: 24 })
    React.useEffect(() => {
        service.cancelConfig((data) => {
            if (data && data.data) {
                setCancelCfg({
                    amount:
                        data.data.cancelRCompanyTax != null
                            ? Number(data.data.cancelRCompanyTax)
                            : 49,
                    hours:
                        data.data.cancelRMaxHourCompany != null
                            ? Number(data.data.cancelRMaxHourCompany)
                            : 24,
                })
            }
        })
    }, [])

    // Tipos de coche
    React.useEffect(() => {
        cartypeService.combo((response: any) => {
            const list: any[] = response?.data || []
            let mapped = list.map((item) => ({
                id: Number(item?.id ?? item?.key ?? item?.value ?? item?.code ?? item?.ID),
                label: String(
                    item?.name ??
                        item?.nombre ??
                        item?.title ??
                        item?.label ??
                        item?.text ??
                        ''
                ).trim(),
            }))

            if (mapped.every((type) => !Number.isFinite(type.id)) && mapped.length) {
                const idByLabel: Record<string, number> = {
                    [normalizeText('Grande')]: 1,
                    [normalizeText('Mediano')]: 2,
                    [normalizeText('Pequeño')]: 3,
                }
                mapped = mapped.map((type) => ({
                    id: idByLabel[normalizeText(type.label)] ?? NaN,
                    label: type.label,
                }))
            }
            setTypes(mapped.filter((t) => Number.isFinite(t.id) && t.label))
        })
    }, [])

    const selectOne = (selector: string) =>
        document.querySelector(selector) as | HTMLInputElement | HTMLSelectElement | null

    const readStartEnd = () => {
        const startInput =
            (selectOne('input[name="start"]') as HTMLInputElement | null) ||
            (document.querySelectorAll(
                'input[type="date"],input[type="datetime-local"]'
            )[0] as HTMLInputElement | null)
        const endInput =
            (selectOne('input[name="end"]') as HTMLInputElement | null) ||
            (document.querySelectorAll(
                'input[type="date"],input[type="datetime-local"]'
            )[1] as HTMLInputElement | null)

        const startIso = toIso(startInput)
        const endIso = toIso(endInput)

        let days = 0
        if (startIso && endIso) {
            const ms = new Date(endIso).getTime() - new Date(startIso).getTime()
            days = ms > 0 ? Math.max(1, Math.ceil(ms / 86400000)) : 0
        }
        return { startIso, endIso, days }
    }

    const readCarTypeId = (): number | null => {
        // 1) id directo
        const selectors = [
            'input[name="carTypes.id"]',
            'select[name="carTypes.id"]',
            'input[name*="carTypes"][name$=".id"]',
            'select[name*="carTypes"][name$=".id"]',
            'input[type="hidden"][name*="carTypes"]',
        ]
        for (const selector of selectors) {
            const element = selectOne(selector)
            const raw = (element?.value || '').trim()
            if (/^\d+$/.test(raw)) return Number(raw)
        }
        // 2) etiqueta del Autocomplete
        const normalizedTypes = types.map((type) => ({ id: type.id, normalizedLabel: normalizeText(type.label) }))
        const autoInputs = Array.from(
            document.querySelectorAll('input[aria-autocomplete="list"]')
        ) as HTMLInputElement[]
        for (const inputEl of autoInputs) {
            const normalizedInput = normalizeText(inputEl.value)
            const match = normalizedTypes.find((t) => t.normalizedLabel === normalizedInput)
            if (match) return match.id
        }
        const buttons = Array.from(document.querySelectorAll('div[role="button"][aria-haspopup="listbox"]')) as HTMLElement[]
        for (const button of buttons) {
            const normalizedText = normalizeText(button.textContent || '')
            const match = normalizedTypes.find((t) => t.normalizedLabel === normalizedText)
            if (match) return match.id
        }
        return null
    }

    const buildPayload = () => {
        const { startIso, endIso, days } = readStartEnd()
        const seguroEl = document.querySelector<HTMLInputElement>('input[type="checkbox"][name="seguro"]')
        const seguro = !!seguroEl?.checked
        const idType = readCarTypeId()
        return { start: startIso, end: endIso, carTypes: idType ? { id: idType } : null, seguro, days }
    }

    const valid = (p: any) => !!(p?.start && p?.end && p?.carTypes?.id)

    const calculate = (opts?: { silent?: boolean }) => {
        const p = buildPayload()
        setWarn(null)

        if (!valid(p)) {
            setTotal(0)
            setDay(0)
            setComputed(false)
            if (!opts?.silent) {
                setWarn(
                    t('rent.price.warnIncomplete')
                )
            }
            return
        }

        setLoading(true)
        service.calculateprice(
            {
                start: p.start,
                end: p.end,
                carTypes: p.carTypes,
                seguro: p.seguro,
                // MOBD-99: los dos puntos van al cálculo para que el total ya traiga el coste de zona.
                // Del punto solo se manda el id de la zona y la coordenada: el precio lo pone el
                // backend leyéndolo de BD, nunca el navegador.
                ...zonePayload(points),
            },
            (response: any, error: any) => {
                setLoading(false)
                if (error || !response || !response.data) {
                    setTotal(0)
                    setDay(0)
                    setComputed(false)
                    if (!opts?.silent) {
                        // MOBD-66: si el backend bloquea al cliente (datos de facturación /
                        // documentación / pago pendientes), se muestra el aviso enriquecido que ya
                        // trae redactado; si no es un bloqueo, el aviso genérico de cálculo de antes.
                        const blockInfo = resolveBlock(error)
                        if (blockInfo) {
                            setWarn(null)
                            showBlock(blockInfo)
                        } else {
                            // MOBD-66: el contrato del servicio expone el texto en `mes` (nunca en `message`), asi que el
                            // motivo del backend se perdia siempre y caia al generico.
                            setWarn(error?.mes || response?.message || t('rent.price.calcError'))
                        }
                    }
                    return
                }
                const total = response.data?.total ?? response.data?.totalAmount ?? response.data?.price?.total ?? 0
                setTotal(total)
                setComputed(true)
                // El precio/día es el del alquiler: el coste de zona es un cargo fijo por servicio y
                // repartirlo entre los días daría una cifra que no significa nada.
                const rentalOnly = Math.max(0, total - zoneCostOf(points))
                setDay(p.days > 0 ? rentalOnly / p.days : 0)
            }
        )
    }

    // Listeners + 1er cálculo silencioso
    React.useEffect(() => {
        const handler = () => {
            clearTimeout((handler as any).t)
            ;(handler as any).t = setTimeout(() => calculate({ silent: true }), 250)
        }

        type L = { el: Element; evt: string; fn: EventListener }
        const listeners: L[] = []
        const add = (el: Element | null, evt: string, fn: EventListener) => {
            if (!el) return
            el.addEventListener(evt, fn)
            listeners.push({ el, evt, fn })
        }

        // Fechas
        document
            .querySelectorAll('input[type="date"],input[type="datetime-local"]')
            .forEach((el) => add(el, 'input', handler))

        add(document.querySelector('input[name="start"]'), 'input', handler)
        add(document.querySelector('input[name="end"]'), 'input', handler)

        // carType id directo
        add(
            document.querySelector('input[name="carTypes.id"]'),
            'input',
            handler
        )
        add(
            document.querySelector('select[name="carTypes.id"]'),
            'change',
            handler
        )
        document
            .querySelectorAll('input[name*="carTypes"][name$=".id"]')
            .forEach((el) => add(el, 'input', handler))
        document
            .querySelectorAll('select[name*="carTypes"][name$=".id"]')
            .forEach((el) => add(el, 'change', handler))

        // Autocomplete (input/change/blur/keydown)
        document
            .querySelectorAll('input[aria-autocomplete="list"]')
            .forEach((el) => {
                add(el, 'input', handler)
                add(el, 'change', handler)
                add(el, 'blur', handler)
                add(el, 'keydown', handler)
            })
        // Click en opciones del dropdown
        const onListboxClick = (e: Event) => {
            const t = e.target as Element | null
            if (t && t.closest('li[role="option"]')) calculate({ silent: true })
        }
        document.addEventListener('click', onListboxClick, true)

        // Seguro
        add(document.querySelector('input[type="checkbox"][name="seguro"]'), 'change', handler)

        // Primer cálculo silencioso
        calculate({ silent: true })

        return () => {
            listeners.forEach(({ el, evt, fn }) =>
                el.removeEventListener(evt, fn)
            )
            clearTimeout((handler as any).t)
            document.removeEventListener('click', onListboxClick, true)
        }
        // `points` entra en las dependencias para que mover un pin recalcule el precio al momento:
        // ver el coste de zona ANTES de confirmar es justamente lo que pide la tarjeta.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [types, points])

    return (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('rent.price.title')}
                </Typography>
                {loading && <CircularProgress size={18} />}
                <Box flexGrow={1} />
                <Button
                    size="small"
                    variant="outlined"
                    onClick={() => calculate()}
                >
                    {t('rent.price.recalculate')}
                </Button>
            </Stack>

            <Stack direction="row" spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {computed && <Chip label={`${t('rent.price.day')}: ${eur(day)}`} />}
                {zoneCostOf(points) > 0 && (
                    <Chip
                        color="warning"
                        label={`${t('rent.zone.cost')}: ${eur(zoneCostOf(points))}`}
                    />
                )}
                {computed && (
                    <Chip color="success" label={`${t('rent.price.total')}: ${eur(total)}`} />
                )}
            </Stack>

            <Alert severity="info" sx={{ mt: 2 }}>
                {t('rent.price.infoConfirmation', {
                    hours: cancelCfg.hours,
                    amount: cancelCfg.amount,
                })}
            </Alert>

            {warn && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                    {warn}
                </Alert>
            )}
        </Paper>
    )
}


/**
 * Portal debajo del checkbox “seguro”: mete el selector de puntos y el cuadro de precio DENTRO del
 * formulario. Van aquí, y no colgando del CrudForm por debajo, para que la pantalla se lea de arriba
 * abajo de una vez (datos → recogida y devolución → precio) y los botones Volver/Guardar queden al
 * final de todo, que es donde el usuario los busca al terminar de rellenar.
 */
function FormExtrasPortal({
    points,
    onChangePoints,
    readOnly,
}: {
    points: PointsValue
    onChangePoints: React.Dispatch<React.SetStateAction<PointsValue>>
    readOnly: boolean
}): JSX.Element | null {
    const [anchor, setAnchor] = React.useState<HTMLElement | null>(null)

    React.useEffect(() => {
        const place = () => {
            const seguroInput = document.querySelector('input[type="checkbox"][name="seguro"]')
            if (!seguroInput) return null
            const gridItem = (seguroInput as HTMLElement).closest(
                'div.MuiGrid-item'
            ) as HTMLElement | null
            if (!gridItem || !gridItem.parentElement) return null

            const holder = document.createElement('div')
            holder.className = 'MuiGrid-item MuiGrid-grid-xs-12'
            holder.style.width = '100%'
            gridItem.parentElement.insertBefore(holder, gridItem.nextSibling)
            return holder
        }

        let h = place()
        if (!h) {
            const mo = new MutationObserver(() => {
                h = place()
                if (h) {
                    setAnchor(h)
                    mo.disconnect()
                }
            })
            mo.observe(document.body, { childList: true, subtree: true })
            return () => mo.disconnect()
        } else {
            setAnchor(h)
        }
    }, [])

    if (!anchor) return null
    return ReactDOM.createPortal(
        <>
            <ZonePointPicker value={points} onChange={onChangePoints} readOnly={readOnly} />
            <PriceBox points={points} />
        </>,
        anchor
    ) as unknown as JSX.Element
}


//Form original (mínimo necesario)
// MOBD-100: street/city/postalCode siguen en el objeto porque el backend los guarda, pero ya NO se
// escriben a mano: los rellena el punto elegido sobre el mapa (dirección geocodificada).
const initElement = {
    user: { id: null, name: '' },
    carTypes: { id: null, name: '' },
    start: '',
    end: '',
    street: '',
    postalCode: '',
    city: '',
    seguro: false,
}

const toIsoUtc = (value?: string) => {
    if (!value) return null
    const d = new Date(value) // datetime-local -> local time
    if (isNaN(d.getTime())) return null
    // ISO sin milisegundos, en UTC
    const pad = (n: number) => String(n).padStart(2, '0')
    return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(
        d.getUTCDate()
    )}T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(
        d.getUTCSeconds()
    )}Z`
}

export default function FormRent() {
    const navigate = useNavigate()
    type FormParms = { id: string }
    let { id } = useParams<FormParms>()
    const { t } = useTranslation()

    // MOBD-100: los dos puntos del mapa. Viven aquí (y no dentro del CrudForm) porque el formulario
    // genérico solo sabe pintar campos de su `estructureform`; el selector se cuelga por `haveInside`
    // y desde aquí alimenta al validador, al guardado y al cuadro de precio.
    const [points, setPoints] = React.useState<PointsValue>({ start: null, end: null })
    const isEdit = !!(id && +id > 0)

    const line = { concept: '', price: 0 }
    const stateForm = {
        openConfirm: false,
        openAddUser: false,
        openCancel: false,
        openFinish: false,
        openCar: false,
        openAddline: false,
        openModalConfirmPay: false,
        openModalConfirmFree: false,
        openModalPay: false,
        openInit: false,
        carSelect: { id: -1, name: '' },
        addline: { ...line },
        userSelected: { id: -1, name: '' },
        opt: { carsOptions: [], users: [] },
    }

    // MOBD-100: memoizados los DOS. CrudForm hace `setState({objectform, reload:true})` en un
    // useEffect que depende de la REFERENCIA de estas props; pasándole un literal nuevo en cada
    // render, cualquier re-render del formulario (por ejemplo al marcar un punto en el mapa) le
    // reseteaba los campos ya rellenos. El bug estaba latente: antes esta pantalla no tenía estado
    // propio y no se re-renderizaba nunca.
    const objectform = React.useMemo(() => ({ ...initElement }), [])

    const estructureform = React.useMemo(
        () => [
            {
                type: 'autocomplete',
                name: 'user.id',
                colSpan: '50%',
                service: usercompaniesService.combouser,
                readonly: id && +id > 0,
                col: 3,
            },
            { type: 'input', name: 'start', inputtype: 'datetime-local', col: 3, step: 300 },
            { type: 'input', name: 'end', inputtype: 'datetime-local', col: 3, step: 300 },
            { type: 'title', name: 'titlevehicle', col: 12 },
            {
                type: 'autocomplete',
                name: 'carTypes.id',
                service: cartypeService.combo,
                readonly: id && +id > 0,
                col: 4,
            },
            // Aquí estaban calle / código postal / ciudad en texto libre. Se piden ahora sobre el
            // mapa (ZonePointPicker, abajo): así se valida que el punto esté en zona de servicio y
            // se ve su coste antes de confirmar, que es lo que MPDD-54 pedía arreglar.
            { type: 'title', name: 'othersinfo', col: 12 },
            { type: 'checkbox', name: 'seguro', readonly: id && +id > 0, col: 3 },
        ],
        [id]
    )

    const isValid = function (object: any) {
        if (!object?.user?.id) return t('rent.error.user')
        if (!object?.start || !object?.end) return t('rent.validate.missingStartEnd')

        // Aquí llega el valor del input, que puede ser 'YYYY-MM-DDTHH:mm' (sin Z)
        const s = new Date(object.start)
        const e = new Date(object.end)

        if (isNaN(s.getTime()) || isNaN(e.getTime())) {
            return t('rent.validate.invalidDate')
        }
        if (e.getTime() <= s.getTime()) {
            return t('rent.validate.endBeforeStart')
        }

        // Fuerza intervalos de 5 min Para evitar horas raras como 9:07
        const STEP_SECONDS = 300
        const stepOk = (d: Date) => (Math.floor(d.getTime() / 1000) % STEP_SECONDS) === 0
        if (!stepOk(s) || !stepOk(e)) {
            return t('rent.validate.step5')
        }

        // MOBD-100: los dos puntos son obligatorios y tienen que caer dentro del mapa operativo.
        // "Fuera de zona = bloqueo total" (MPDD-54). El backend lo vuelve a comprobar en el alta: esto
        // es solo para no hacer ir y volver al servidor y poder decir exactamente qué falta.
        // Solo al PEDIR el rent: los ya creados se consultan en modo lectura y los antiguos ni
        // siquiera tienen coordenadas, asi que exigirselas seria pedir algo imposible.
        if (isEdit) return null

        if (!points.start) return t('rent.zone.errorPickupMissing')
        if (!points.end) return t('rent.zone.errorDropoffMissing')
        if (!points.start.zone) return t('rent.zone.errorPickupOutside')
        if (!points.end.zone) return t('rent.zone.errorDropoffOutside')

        return null
    }

    const confirmNew = () => navigate(-1)

    /**
     * MOBD-100: al abrir un rent ya creado se reconstruyen sus dos puntos para verlos en el mapa (en
     * modo lectura). Los rents antiguos no tienen coordenadas: en esos el mapa sale vacío y la
     * dirección se sigue viendo en la ficha, sin inventarse una ubicación que nadie marcó.
     */
    const recoveryElement = (objectedit: any, element: any) => {
        const rebuild = (
            address: string,
            lat: number,
            lng: number,
            zone: any,
            frozenPrice: any
        ) => {
            // Los rents viejos guardaron 0/0 en vez de NULL cuando no había ubicación (los hay en
            // producción). Sin este filtro se pintaría un pin en mitad del Atlántico marcado como
            // "fuera de zona", que es peor que no enseñar nada.
            if (lat == null || lng == null) return null
            if (Number(lat) === 0 && Number(lng) === 0) return null
            return {
                address: address || '',
                lat: Number(lat),
                lng: Number(lng),
                city: element.city || '',
                postalCode: element.postalCode || '',
                zone: zone
                    ? {
                          id: zone.id,
                          name: zone.name,
                          // El coste que se muestra es el CONGELADO del rent, no el precio que la zona
                          // tenga hoy: es lo que se le cobró a la empresa.
                          price:
                              frozenPrice != null
                                  ? Number(frozenPrice)
                                  : zone.price != null
                                  ? Number(zone.price)
                                  : 0,
                          color: zone.color,
                      }
                    : null,
            }
        }

        setPoints({
            start: rebuild(
                element.street,
                element.lat_point_start,
                element.lng_point_start,
                element.zoneStarReserve,
                element.zoneStartPrice
            ),
            end: rebuild(
                element.streetend,
                element.lat_point_end,
                element.lng_point_end,
                element.zoneEndReserve,
                element.zoneEndPrice
            ),
        })

        return objectedit
    }

    return (
        <>
            <CrudForm
                objectform={objectform}
                estructureform={estructureform}
                i18n={'rent'}
                urledit={'/rent/edit/'}
                urlCancel={'/rent'}
                confirmNew={confirmNew}
                service={service}
                valid={isValid}
                stateform={stateForm}
                recoveryElement={recoveryElement}
                beforeSave={(object: any) => {
                    const out = { ...object }
                    out.start = toIsoUtc(out.start)
                    out.end = toIsoUtc(out.end)

                    // MOBD-98: la ubicación normalizada de cada punto (dirección geocodificada +
                    // coordenada + zona). `street` es la recogida y `streetend` la devolución, que es
                    // como las tenía ya la entidad Rental. Solo en el alta: al consultar un rent
                    // existente no hay nada que reescribir y machacarlo con nulos seria destructivo.
                    if (!isEdit) {
                        Object.assign(out, zonePayload(points))
                        out.street = points.start ? points.start.address : ''
                        out.streetend = points.end ? points.end.address : ''
                        // Población y CP salen del propio punto de recogida (los daba el geocodificador),
                        // así que se siguen guardando como hasta ahora pero sin erratas de tecleo.
                        out.city = points.start ? points.start.city : ''
                        out.postalCode = points.start ? points.start.postalCode : ''
                    }

                    return out
                }}
            />
            <FormExtrasPortal
                points={points}
                onChangePoints={setPoints}
                readOnly={!!isEdit}
            />
        </>
    )
}
